import { defaultPosition } from '../dropdownConsts';
import {
  ReactElement,
  RefObject,
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react';
import { ClientRect } from './modalDropdownTypes';
import { Notifier } from './types';

export type GetBox = () => ClientRect;

type WithDropdownRenderProps<T extends HTMLElement> = (
  openModal: VoidFunction,
  closeModal: VoidFunction,
  isOpen: boolean,
  getHostBox: GetBox,
  hostRef: RefObject<T | null>
) => ReactElement<any>;

type WithDropdownProps<T extends HTMLElement> = {
  children: WithDropdownRenderProps<T>;
  onMenuOpen?: () => void | Promise<void>;
  onMenuClose?: () => void;
  requestOpen?: Notifier;
};

export const WithDropdown = <T extends HTMLElement>({
  onMenuOpen,
  onMenuClose,
  children,
  requestOpen: onOpened,
}: WithDropdownProps<T>) => {
  const [isOpen, setIsOpen] = useState(false);
  const hostRef = useRef<T | null>(null);

  const openModal = useCallback(() => {
    setIsOpen(true);
    onMenuOpen?.();
  }, [onMenuOpen]);

  const closeModal = useCallback(() => {
    setIsOpen(false);
    onMenuClose?.();
    if (hostRef.current) {
      hostRef.current.focus();
    }
  }, [onMenuClose]);

  const getHostBox: GetBox = useCallback(
    () => hostRef.current?.getBoundingClientRect() ?? defaultPosition,
    [hostRef]
  );

  useEffect(() => {
    onOpened?.subscribe(openModal);
    return () => onOpened?.unsubscribe(openModal);
  }, [onOpened, openModal]);

  return children(openModal, closeModal, isOpen, getHostBox, hostRef);
};
